/**
 * Role Management Component
 * Allows admins to assign roles to users and manage Auth0 metadata
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import axios from 'axios';

const RoleManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newPermissions, setNewPermissions] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Available job roles (what they do)
  const roles = [
    { value: 'Director', label: 'Director', color: 'error' },
    { value: 'Manager', label: 'Manager', color: 'warning' },
    { value: 'Senior Project Manager', label: 'Senior Project Manager', color: 'info' },
    { value: 'Project Manager', label: 'Project Manager', color: 'primary' },
    { value: 'Junior Project Manager', label: 'Junior Project Manager', color: 'default' },
    { value: 'Senior Quantity Surveyor', label: 'Senior Quantity Surveyor', color: 'info' },
    { value: 'Quantity Surveyor', label: 'Quantity Surveyor', color: 'primary' },
    { value: 'Junior Quantity Surveyor', label: 'Junior Quantity Surveyor', color: 'default' },
    { value: 'Trainee Quantity Surveyor', label: 'Trainee Quantity Surveyor', color: 'default' },
    { value: 'Project Administrator', label: 'Project Administrator', color: 'warning' },
    { value: 'General Administration', label: 'General Administration', color: 'default' }
  ];

  // Available permission levels (what they can access)
  const permissionLevels = [
    { value: 'admin', label: 'Administrator Access', color: 'error', description: 'Full system access including user management' },
    { value: 'user', label: 'Standard User Access', color: 'primary', description: 'Access to assigned projects and time tracking' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users', { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !newRole || !newPermissions) {
      showSnackbar('Please select a user, role, and permissions', 'error');
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post('/api/admin/assign-role', {
        userId: selectedUser.auth0_user_id || selectedUser.id,
        role: newRole,
        permissions: newPermissions,
        employeeId: selectedUser.id
      }, { withCredentials: true });

      if (response.data.success) {
        showSnackbar(`Role updated to ${newRole} successfully!`, 'success');
        
        // Update local user data
        setUsers(users.map(user =>
          user.id === selectedUser.id
            ? { ...user, role: newRole, permissions: newPermissions }
            : user
        ));
        
        // Show instructions for user to re-login
        showSnackbar(
          'Role updated! User must log out and log back in to see changes.', 
          'info'
        );
        
        setOpenDialog(false);
        setSelectedUser(null);
        setNewRole('');
        setNewPermissions('');
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      showSnackbar(
        error.response?.data?.error || 'Error assigning role', 
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshUserMetadata = async (user) => {
    try {
      setLoading(true);
      
      const response = await axios.get(`/api/admin/user-metadata/${user.id}`, {
        withCredentials: true
      });
      
      console.log('User Auth0 metadata:', response.data);
      showSnackbar('User metadata refreshed - check console for details', 'info');
      
    } catch (error) {
      console.error('Error fetching user metadata:', error);
      showSnackbar('Error fetching user metadata', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getRoleColor = (role) => {
    const roleConfig = roles.find(r => r.value === role);
    return roleConfig?.color || 'default';
  };

  const getRoleLabel = (role) => {
    const roleConfig = roles.find(r => r.value === role);
    return roleConfig?.label || role;
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" gutterBottom>
              <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Role Management
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchUsers}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Job Role</TableCell>
                  <TableCell>System Permissions</TableCell>
                  <TableCell>Auth0 Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role || 'Not Set'}
                        color="primary"
                        size="small"
                        icon={<UserIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.permissions === 'admin' ? 'Administrator' : 'Standard User'}
                        color={user.permissions === 'admin' ? 'error' : 'primary'}
                        size="small"
                        icon={user.permissions === 'admin' ? <AdminIcon /> : <UserIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.auth0_user_id ? 'Linked' : 'Not Linked'}
                        color={user.auth0_user_id ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedUser(user);
                          setNewRole(user.role);
                          setOpenDialog(true);
                        }}
                        disabled={!user.auth0_user_id}
                        sx={{ mr: 1 }}
                      >
                        Change Role
                      </Button>
                      <Tooltip title="View Auth0 Metadata">
                        <IconButton
                          size="small"
                          onClick={() => handleRefreshUserMetadata(user)}
                          disabled={!user.auth0_user_id}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Role Assignment Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Role to {selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Job Role</InputLabel>
              <Select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                label="Job Role"
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>System Permissions</InputLabel>
              <Select
                value={newPermissions}
                onChange={(e) => setNewPermissions(e.target.value)}
                label="System Permissions"
              >
                {permissionLevels.map((permission) => (
                  <MenuItem key={permission.value} value={permission.value}>
                    <Box>
                      <Typography variant="body1">{permission.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {permission.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Job Role:</strong> Defines their position (PM, QS, Director, etc.)<br/>
            <strong>System Permissions:</strong> Controls what they can access in the system<br/>
            After changing permissions, the user must log out and log back in to see the changes.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAssignRole}
            variant="contained"
            disabled={loading || !newRole || !newPermissions}
          >
            Assign Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleManagement;
